import type {
	BuildProgress,
	ProjectBuildGuide,
} from "../../domain/build/getProjectBuildGuide";
import type { Market } from "../../domain/location/types";
import type { CalculationResult } from "../../engines/types";
import { type Dictionary, formatPrice } from "../../i18n";
import { pluralizeCount } from "../../i18n/plural";
import { getMarketProfile } from "../../market/marketProfile";
import { collectSafetyFlags } from "./safetyFlags";
import type { BuilderWorkspace } from "./types";

/**
 * Pure model mapper for the workspace-nav value badges and the summary-rail
 * completeness signal. React-free and side-effect-free: it consumes the
 * already-computed `calculateMaterials()` result (`derived.calcData`) plus the
 * persisted `buildProgress`, and returns fully-formed display + a11y strings.
 *
 * Nothing here recomputes materials or touches the Zustand store — badges are
 * a presentation layer over data BuilderPage has already memoized. See
 * docs/notes/2026-06-24-cross-cutting-insight-views-need-a-pure-model-mapper.md.
 */

/** Drives the badge's muted tone. `warning` is the only non-neutral state. */
export type WorkspaceBadgeStatus = "neutral" | "warning";

export interface WorkspaceBadge {
	/** Compact visible glyph/number, e.g. "47", "✓", "3/8". Decorative. */
	text: string;
	status: WorkspaceBadgeStatus;
	/**
	 * Spoken form folded into the tab's aria-label so screen readers announce
	 * the value once, in words — never the bare glyph. e.g. "47 rader".
	 */
	ariaText: string;
}

export type WorkspaceBadges = Partial<Record<BuilderWorkspace, WorkspaceBadge>>;

interface WorkspaceBadgeInput {
	calcData: CalculationResult;
	/** The guide the Build tab renders — the badge counts its steps. */
	buildGuide?: ProjectBuildGuide;
	buildProgress: BuildProgress;
	t: Dictionary;
	market: Market;
}

/**
 * Count of the guide's steps the user has ticked complete. Only steps still in
 * the guide count: progress saved against a step the current design no longer
 * has must not push the badge past the guide's own total.
 */
function countCompletedSteps(
	buildGuide: ProjectBuildGuide,
	buildProgress: BuildProgress,
): number {
	return buildGuide.steps.filter((step) => buildProgress[step.id]?.completed)
		.length;
}

export function buildWorkspaceBadges({
	calcData,
	buildGuide,
	buildProgress,
	t,
	market,
}: WorkspaceBadgeInput): WorkspaceBadges {
	const badges: WorkspaceBadges = {};
	const { locale } = getMarketProfile(market);

	// Each slice is guarded even though the production enrichment chain always
	// supplies them — a degraded/partial calcData (recovery paths, tests) must
	// yield fewer badges, never a throw. Mirrors the estimatedTotal `?.` guard.

	// INKÖP — bill-of-materials row count.
	const rowCount = calcData.materials?.length ?? 0;
	if (rowCount > 0) {
		badges.shopping = {
			text: String(rowCount),
			status: "neutral",
			ariaText: pluralizeCount(t, "workspace_badgeRows", rowCount, locale),
		};
	}

	// KAPNOTA — cut-list part-line count. Reuses the row unit: the tab's
	// aria-label already carries the workspace name ("Kapnota, 12 rader").
	const cutCount = calcData.cuts?.length ?? 0;
	if (cutCount > 0) {
		badges.cutlist = {
			text: String(cutCount),
			status: "neutral",
			ariaText: pluralizeCount(t, "workspace_badgeRows", cutCount, locale),
		};
	}

	// PRIS — estimated material cost in the market currency.
	const cost = calcData.pricingSummary?.materialCostSek ?? 0;
	if (cost > 0) {
		const formatted = formatPrice(cost, market);
		badges.pricing = {
			text: formatted,
			status: "neutral",
			ariaText: formatted,
		};
	}

	// REGLER — ✓ when clear, else the number of flags raised.
	const permit = calcData.permit;
	if (permit) {
		// Counted from the same list the design panel shows.
		const flagCount = collectSafetyFlags(calcData, market, t).length;
		if (flagCount === 0) {
			badges.rules = {
				text: "✓",
				status: "neutral",
				ariaText: t.workspace_badgeRulesOk,
			};
		} else {
			badges.rules = {
				text: String(flagCount),
				status: "warning",
				ariaText: pluralizeCount(
					t,
					"workspace_badgeRulesFlags",
					flagCount,
					locale,
				),
			};
		}
	}

	// BYGG — progress (completed/total) once the user has ticked any step,
	// otherwise the guide's step count. Counted off the same guide the Build
	// tab renders ("Steg 1 av N"), never off the timeline, which groups work
	// differently and put 9 on the tab above a 13-step guide.
	const totalSteps = buildGuide?.steps.length ?? 0;
	if (buildGuide && totalSteps > 0) {
		const completed = countCompletedSteps(buildGuide, buildProgress);
		if (completed > 0) {
			badges.build = {
				text: `${completed}/${totalSteps}`,
				status: "neutral",
				ariaText: `${completed} ${t.workspace_badgeStepsDoneOf} ${totalSteps}`,
			};
		} else {
			badges.build = {
				text: String(totalSteps),
				status: "neutral",
				ariaText: `${totalSteps} ${t.workspace_badgeStepsUnit}`,
			};
		}
	}

	return badges;
}

export interface CompletenessSignal {
	/** 0–100, rounded. */
	percent: number;
	/** Already-localized labels for the decisions still unset. */
	missingLabels: string[];
	/** One-line summary, e.g. "Design 80 % — trall och postnummer saknas". */
	summary: string;
}

interface CompletenessInput {
	/**
	 * Where the sauna stands is set: a postal code on se (snow zone and permit
	 * rules hang off it), the climate profile on intl (always has a value).
	 */
	hasLocation: boolean;
	hasOpenings: boolean;
	t: Dictionary;
}

/**
 * Design-completeness: the decisions a valid plan still needs (an opening, the
 * location its loads and rules come from). One rail line, not a new panel.
 * Size/roof/heater always carry sensible defaults, and an optional extra such
 * as a deck never counts against a design — "33 %, decking still to add" read
 * as a failing grade for a sauna that needs no deck.
 */
export function buildCompletenessSignal({
	hasLocation,
	hasOpenings,
	t,
}: CompletenessInput): CompletenessSignal {
	const checklist = [hasOpenings, hasLocation];
	const done = checklist.filter(Boolean).length;
	const percent = Math.round((done / checklist.length) * 100);

	const missingLabels: string[] = [];
	if (!hasOpenings) missingLabels.push(t.completeness_missingOpenings);
	if (!hasLocation) missingLabels.push(t.completeness_missingPostal);

	const summary =
		missingLabels.length === 0
			? `${t.completeness_label} ${percent}% — ${t.completeness_complete}`
			: `${t.completeness_label} ${percent}% — ${missingLabels.join(t.completeness_join)} ${t.completeness_missingSuffix}`;

	return { percent, missingLabels, summary };
}
